// Human-readable model parameter values (owner 2026-11-01: exported
// component properties "don't show a number per unit but cast as a human
// readable unit").
//
// The Revit plugin and rvtread export numeric parameters as Revit INTERNAL
// units (feet, ft2, ft3, radians, and SI-in-feet for electrical/HVAC: a
// voltage is volts x 10.7639) with no spec type beside them, so the type is
// recognised from the parameter name. Conservative on purpose: a value is
// converted only when the name is unambiguous, and voltages only when the
// result is a standard voltage. Raw values stay available to callers.
// CommonJS so the backend can require the same file (back/utils/paramUnits.js).
const FT2 = 10.7639104;        // SI-in-feet factor (1 m2 in ft2)
const STD_VOLTS = [12, 24, 48, 110, 115, 120, 125, 200, 208, 220, 230, 240, 277, 347, 380, 400, 415, 440, 460, 480, 575, 600, 690, 2400, 4160, 4800, 12470, 13200, 13800, 24940, 34500];

const round = (v, d) => { const f = 10 ** d; return Math.round(v * f) / f; };
const num = (v, d = 2) => round(v, d).toLocaleString('en-US', { maximumFractionDigits: d });

function feetInches(ft) {
  const neg = ft < 0; let a = Math.abs(ft);
  let feet = Math.floor(a); let inches = (a - feet) * 12;
  let sixteenths = Math.round(inches * 16);
  if (sixteenths === 192) { feet += 1; sixteenths = 0; }
  const whole = Math.floor(sixteenths / 16); let frac = sixteenths % 16; let den = 16;
  while (frac && frac % 2 === 0) { frac /= 2; den /= 2; }
  const inText = whole ? `${whole}${frac ? ` ${frac}/${den}` : ''}"` : (frac ? `${frac}/${den}"` : '0"');
  return `${neg ? '-' : ''}${feet ? `${feet}' ` : ''}${inText}`;
}

// [test on the name, kind]
const RULES = [
  [/apparent\s*load|\bva\b|kva/i, 'apparentPower'],
  [/(^|[\s_])volt(age)?s?($|[\s_])|voltage/i, 'voltage'],
  [/(^|[\s_])(amps?|amperage|current|fla|mca|mocp|full load amps)($|[\s_])/i, 'current'],
  [/wattage|(^|[\s_])power($|[\s_])|(^|[\s_])watts?($|[\s_])|\bload\b(?!.*classification)/i, 'power'],
  [/air\s*flow|airflow|\bcfm\b/i, 'airflow'],
  [/(^|[\s_])flow($|[\s_])|gpm/i, 'flow'],
  [/pressure|pressure drop|static press/i, 'pressure'],
  [/temperature|\btemp\b/i, 'temperature'],
  [/weight|load capacity/i, 'force'],
  [/(^|[\s_])area($|[\s_])/i, 'area'],
  [/(^|[\s_])volume($|[\s_])/i, 'volume'],
  [/angle|rotation|slope angle/i, 'angle'],
  [/width|height|depth|length|diameter|thickness|radius|offset|elevation|clearance|(^|[\s_])size($|[\s_])|perimeter|insulation thickness|lining thickness/i, 'length'],
];

function kindOf(name) {
  const n = String(name || '');
  if (/id$|(^|[\s_])id($|[\s_])|count|number|(^|[\s_])no\.?($|[\s_])|phase|pole|quantity|year|percent|%|ratio|factor|classification|mark|code/i.test(n)) return null;
  for (const [re, kind] of RULES) if (re.test(n)) return kind;
  return null;
}

/** { text, kind } for a numeric parameter, or null to show it as stored. */
function humanize(name, value, { metric = false } = {}) {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    if (typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value.trim())) value = Number(value); else return null;
  }
  const kind = kindOf(name);
  if (!kind) return null;
  const v = value;
  switch (kind) {
    case 'voltage': {
      const volts = v / FT2;
      const std = STD_VOLTS.find((s) => Math.abs(volts - s) / s < 0.02);
      return std ? { kind, text: `${std} V` } : null;
    }
    case 'apparentPower': { const va = v / FT2; return { kind, text: va >= 1000 ? `${num(va / 1000, 2)} kVA` : `${num(va, 0)} VA` }; }
    case 'power': { const w = v / FT2; return { kind, text: w >= 1000 ? `${num(w / 1000, 2)} kW` : `${num(w, 0)} W` }; }
    case 'current': return { kind, text: `${num(v, 1)} A` };
    case 'airflow': return metric ? { kind, text: `${num(v * 0.0283168 * 1000, 0)} L/s` } : { kind, text: `${num(v * 60, 0)} CFM` };
    case 'flow': return metric ? { kind, text: `${num(v * 28.3168, 2)} L/s` } : { kind, text: `${num(v * 448.831, 1)} GPM` };
    case 'pressure': { const pa = v * 3.28084; return metric ? { kind, text: `${num(pa, 0)} Pa` } : { kind, text: `${num(pa / 249.089, 2)} in wg` }; }
    case 'temperature': { const f = (v - 273.15) * 9 / 5 + 32; return metric ? { kind, text: `${num(v - 273.15, 1)} °C` } : { kind, text: `${num(f, 1)} °F` }; }
    case 'force': { const lbf = v * 0.0685218; return metric ? { kind, text: `${num(lbf * 0.453592, 1)} kg` } : { kind, text: `${num(lbf, 1)} lb` }; }
    case 'area': return metric ? { kind, text: `${num(v * 0.092903, 2)} m²` } : { kind, text: `${num(v, 2)} ft²` };
    case 'volume': return metric ? { kind, text: `${num(v * 0.0283168, 3)} m³` } : { kind, text: `${num(v, 2)} ft³` };
    case 'angle': return { kind, text: `${num((v * 180) / Math.PI, 2)}°` };
    case 'length': return metric ? { kind, text: `${num(v * 304.8, 0)} mm` } : { kind, text: feetInches(v) };
    default: return null;
  }
}

/** A copy of a parameter map with readable values (raw kept under `raw` when asked). */
function humanizeParams(params, opts = {}) {
  const out = {};
  for (const [k, v] of Object.entries(params || {})) {
    const h = humanize(k, v, opts);
    out[k] = h ? (opts.keepRaw ? { value: h.text, raw: v } : h.text) : v;
  }
  return out;
}

module.exports = { humanize, humanizeParams, kindOf, feetInches };
